import React, { useEffect, useId, useState } from 'react';
import { supabase } from '../supabaseClient';

function formatDuration(totalSeconds) {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts = [];
  if (days > 0) parts.push(`${days}j`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(' ');
}

/**
 * HeartbeatMonitor - Displays real-time connection status based on heartbeats
 * Shows online/offline status and health metrics
 */
function HeartbeatMonitor({ distributeurId }) {
  const [heartbeat, setHeartbeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const instanceId = useId();

  useEffect(() => {
    // Load latest heartbeat
    const loadHeartbeat = async () => {
      const { data, error } = await supabase
        .from('heartbeats')
        .select('*')
        .eq('distributeur_id', distributeurId)
        .order('timestamp', { ascending: false })
        .limit(1);

      setHeartbeat(!error && data && data.length > 0 ? data[0] : null);
      setLoading(false);
    };

    loadHeartbeat();

    // Subscribe to new heartbeats
    // Topic must be unique per mounted instance: supabase.channel() reuses
    // any existing channel with the same topic name, and calling .on() on an
    // already-subscribed channel throws. Multiple HeartbeatMonitor instances
    // can be mounted for the same distributeurId at once (list card + modal).
    const channel = supabase
      .channel(`heartbeat-monitor-${distributeurId}-${instanceId.replace(/[^a-zA-Z0-9]/g, '')}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'heartbeats',
        filter: `distributeur_id=eq.${distributeurId}`
      }, (payload) => {
        setHeartbeat(payload.new);
      })
      .subscribe();

    // Tick every second so the elapsed time since the last heartbeat stays live
    const interval = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [distributeurId, instanceId]);

  if (loading) {
    return (
      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
        Chargement...
      </span>
    );
  }

  if (!heartbeat) {
    return (
      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
        Aucun heartbeat reçu
      </span>
    );
  }

  const health = heartbeat.health || {};
  const secondsAgo = Math.floor((now - new Date(heartbeat.timestamp)) / 1000);
  const isOnline = secondsAgo < 90;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/* Online status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isOnline ? '#22c55e' : '#ef4444',
          display: 'inline-block'
        }} />
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isOnline ? '#166534' : '#991b1b' }}>
          {isOnline ? 'En ligne' : 'Hors ligne'}
        </span>
        {!isOnline && (
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            (il y a {formatDuration(secondsAgo)})
          </span>
        )}
      </div>

      {/* Health metrics */}
      {isOnline && Object.keys(health).length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {health.gpio !== undefined && (
            <span style={{
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: 12,
              background: health.gpio ? '#dcfce7' : '#fee2e2',
              color: health.gpio ? '#166534' : '#991b1b'
            }}>
              GPIO {health.gpio ? '✓' : '✗'}
            </span>
          )}
          {health.mqtt !== undefined && (
            <span style={{
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: 12,
              background: health.mqtt ? '#dcfce7' : '#fef3c7',
              color: health.mqtt ? '#166534' : '#92400e'
            }}>
              MQTT {health.mqtt ? '✓' : '✗'}
            </span>
          )}
          {health.cpu_temp != null && (
            <span style={{
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: 12,
              background: '#f3f4f6',
              color: '#374151'
            }}>
              CPU: {health.cpu_temp.toFixed(1)}°C
            </span>
          )}
          {health.disk_usage != null && (
            <span style={{
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: 12,
              background: health.disk_usage > 80 ? '#fee2e2' : '#f3f4f6',
              color: health.disk_usage > 80 ? '#991b1b' : '#374151'
            }}>
              Disk: {health.disk_usage.toFixed(0)}%
            </span>
          )}
          {health.uptime != null && (
            <span style={{
              fontSize: '0.72rem',
              padding: '2px 8px',
              borderRadius: 12,
              background: '#f3f4f6',
              color: '#374151'
            }}>
              Uptime: {Math.floor(health.uptime / 3600)}h
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default HeartbeatMonitor;
